import { useState, useEffect, useRef, useCallback } from 'react'
import {
  View, Text, Pressable, StyleSheet, ScrollView, TextInput,
  Switch, ActivityIndicator, Platform, Image,
} from 'react-native'
import { X, Shield, Check, MapPin, Phone, Info, Plus, Trash2, AlertCircle } from 'lucide-react-native'
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated'
import BottomSheet, { BottomSheetModal, BottomSheetBackdrop, BottomSheetScrollView, BottomSheetView } from '@gorhom/bottom-sheet'
import AddressInput from './AddressInput'
import EnergyBreakdownAccordion, {
  type TariffData,
  DEFAULT_KWH_YEAR,
  getEnergyBreakdown,
  calcEnergyTotal,
} from './EnergyBreakdown'
import { MobileDetailAccordion, InternetDetailAccordion } from './PlanDetailAccordions'
import { appEvents } from '../lib/events'
import { haptics } from '../utils/haptics'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'
import { toast } from './Toast'
import { useResponsive } from '../hooks/useResponsive'
import { getProviderLogo } from '../utils/providerLogos'

// ─── Step Indicator ─────────────────────────────

const STEP_LABELS = ['Oplysninger', 'Betaling', 'Fuldmagt']
const STEP_MAP: Record<string, number> = { main: 0, pbs: 1, fuldmagt: 2 }

function StepIndicator({ currentStep }: { currentStep: 'main' | 'pbs' | 'fuldmagt' }) {
  const current = STEP_MAP[currentStep]
  return (
    <View style={stepStyles.container}>
      {/* Lines layer — rendered BEHIND dots, with half-spacers at edges to align with dot centers */}
      <View style={stepStyles.linesLayer}>
        <View style={{ flex: 0.5 }} />
        {STEP_LABELS.map((_, i) => {
          if (i === 0) return null
          const lineActive = i <= current
          return (
            <View key={`line-${i}`} style={[stepStyles.lineSegment, lineActive ? stepStyles.lineActive : stepStyles.lineFuture]} />
          )
        })}
        <View style={{ flex: 0.5 }} />
      </View>
      {/* Dots + labels layer — rendered ON TOP */}
      {STEP_LABELS.map((label, i) => {
        const isCompleted = i < current
        const isActive = i === current
        const isFuture = i > current
        return (
          <View key={label} style={stepStyles.stepGroup}>
            <View style={[
              stepStyles.dot,
              isCompleted && stepStyles.dotCompleted,
              isActive && stepStyles.dotActive,
              isFuture && stepStyles.dotFuture,
            ]}>
              {isCompleted && <Check size={10} color="#fff" />}
            </View>
            <Text style={[
              stepStyles.label,
              isActive && stepStyles.labelActive,
              isFuture && stepStyles.labelFuture,
            ]}>{label}</Text>
          </View>
        )
      })}
    </View>
  )
}

const stepStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    position: 'relative',
  },
  linesLayer: {
    position: 'absolute',
    top: spacing.md + 10,
    left: spacing.lg,
    right: spacing.lg,
    flexDirection: 'row',
    height: 2,
  },
  lineSegment: {
    flex: 1,
    height: 2,
  },
  lineActive: { backgroundColor: colors.primary },
  lineFuture: { backgroundColor: colors.border },
  stepGroup: {
    alignItems: 'center',
    flex: 1,
  },
  dot: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  dotCompleted: {
    backgroundColor: colors.success,
    borderColor: colors.success,
  },
  dotActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryLight,
  },
  dotFuture: {
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  label: {
    ...typography.small,
    color: colors.text.secondary,
    marginTop: 4,
    textAlign: 'center',
  },
  labelActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  labelFuture: {
    color: colors.text.muted,
  },
})

// ─── Types ───────────────────────────────────────

export interface AcceptableQuote {
  id: string
  provider: string
  product_name?: string
  category: string
  monthly_price: number
  details?: Record<string, any>
  estimated_annual_savings?: number
  creation_fee?: number
}

interface AcceptQuoteModalProps {
  quote: AcceptableQuote
  visible: boolean
  onClose: () => void
  userProfile?: { address?: string; phone?: string; full_name?: string } | null
  tariffs?: TariffData | null
  yearlyKwh?: number
  hasPaymentInfo?: boolean
  existingPaymentReg?: string | null
  existingPaymentAccount?: string | null
  /** Onboarding plans for this category — used to pre-fill extra agreements (plans[1], plans[2], ...) */
  onboardingPlans?: any[]
  onAccept: (data: {
    confirmed_address?: string
    confirmed_phone?: string
    payment_reg?: string
    payment_account?: string
    fuldmagt_accepted?: boolean
    keep_number?: boolean
    use_existing_payment?: boolean
    extra_agreements?: { address?: string; phone?: string; meter_number?: string; keep_number?: boolean }[]
  }) => Promise<void>
}

// ─── Helpers ─────────────────────────────────────

import { getProviderColor } from './QuoteCard'

const getDisplayPrice = (quote: AcceptableQuote) => {
  const estimatedTotal = quote.details?.estimated_total_monthly as number | undefined
  if (estimatedTotal && Math.round(estimatedTotal) !== Math.round(Number(quote.monthly_price))) {
    return Math.round(estimatedTotal)
  }
  return Number(quote.monthly_price)
}

const getPricePrefix = (quote: AcceptableQuote) => (quote.details?.is_spot_based ? '~' : '')

// ─── Component ───────────────────────────────────

export default function AcceptQuoteModal({
  quote,
  visible,
  onClose,
  userProfile,
  tariffs,
  yearlyKwh,
  hasPaymentInfo = false,
  existingPaymentReg,
  existingPaymentAccount,
  onboardingPlans,
  onAccept,
}: AcceptQuoteModalProps) {
  const { isDesktop } = useResponsive()
  const bottomSheetRef = useRef<BottomSheetModal>(null)
  const [step, setStep] = useState<'main' | 'pbs' | 'fuldmagt'>('main')
  const [confirmedAddress, setConfirmedAddress] = useState('')
  const [confirmedPhone, setConfirmedPhone] = useState('')
  const [pbsReg, setPbsReg] = useState('')
  const [pbsAccount, setPbsAccount] = useState('')
  const [pbsErrors, setPbsErrors] = useState<Set<string>>(new Set())
  const [useExistingPayment, setUseExistingPayment] = useState(true)
  const [showNewPbsFields, setShowNewPbsFields] = useState(false)
  const [fuldmagtConsent, setFuldmagtConsent] = useState(false)
  const [extraAgreements, setExtraAgreements] = useState<{ address: string; phone: string; meter_number: string; keep_number: boolean }[]>([])
  const [keepNumber, setKeepNumber] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successShown, setSuccessShown] = useState(false)

  // Control bottom sheet via visible prop
  useEffect(() => {
    if (visible) {
      // Use rAF to ensure the BottomSheetModal ref is fully committed before calling present()
      const handle = requestAnimationFrame(() => {
        bottomSheetRef.current?.present()
        haptics.medium()
      })
      return () => cancelAnimationFrame(handle)
    }
  }, [visible])

  // Pre-fill from user profile
  useEffect(() => {
    if (userProfile) {
      setConfirmedAddress(userProfile.address || '')
      // Strip country code prefix (+45/0045) and format as "12 34 56 78"
      const rawPhone = (userProfile.phone || '').replace(/^(?:\+?45|0045)\s*/, '').replace(/[^0-9]/g, '').slice(0, 8)
      setConfirmedPhone(rawPhone.replace(/(\d{2})(?=\d)/g, '$1 ').trim())
    }
  }, [userProfile])

  // Timer refs for cleanup
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current)
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current)
    }
  }, [])

  // Reset when modal opens
  useEffect(() => {
    if (visible) {
      setStep('main')
      setFuldmagtConsent(false)
      setPbsReg('')
      setPbsAccount('')
      setUseExistingPayment(true)
      setShowNewPbsFields(false)
      setIsSubmitting(false)
      setSuccessShown(false)
      setKeepNumber(true)
    }
  }, [visible])

  const isEnergy = quote.category === 'energy'
  const isMobile = quote.category === 'mobile'
  const isInternet = quote.category === 'internet'
  const showAddress = isEnergy || isInternet
  const showPhone = isMobile

  // Pre-fill extra agreements from onboarding plans (plans[1], plans[2], ...)
  useEffect(() => {
    if (onboardingPlans && onboardingPlans.length > 1) {
      const extras = onboardingPlans.slice(1).map((plan: any) => ({
        address: (showAddress && plan.address && !plan.same_address) ? plan.address : '',
        phone: showPhone ? (plan.phone || '') : '',
        meter_number: '',
        keep_number: true,
      }))
      setExtraAgreements(extras)
    }
  }, [onboardingPlans, showAddress, showPhone])

  const breakdown = isEnergy ? getEnergyBreakdown(quote, tariffs || null, yearlyKwh) : undefined
  const displayPrice = (isEnergy && breakdown && tariffs)
    ? calcEnergyTotal(breakdown, tariffs, yearlyKwh || DEFAULT_KWH_YEAR)
    : getDisplayPrice(quote)
  const savings = Number(quote.estimated_annual_savings) || 0
  const monthlySavings = Math.round(savings / 12)
  const providerColor = getProviderColor(quote.provider)
  const providerLogo = getProviderLogo(quote.provider)

  const handleConfirm = () => {
    if (showAddress && !confirmedAddress.trim()) {
      toast.error('Udfyld din adresse for at fortsætte')
      haptics.error()
      return
    }
    if (showPhone && keepNumber) {
      const phoneDigits = confirmedPhone.replace(/\D/g, '')
      if (!phoneDigits) {
        toast.error('Udfyld dit telefonnummer for at fortsætte')
        haptics.error()
        return
      }
      if (phoneDigits.length !== 8) {
        toast.error('Telefonnummer skal være 8 cifre')
        haptics.error()
        return
      }
    }

    haptics.light()
    setPbsErrors(new Set())
    setStep('pbs')
  }

  const handleFinalAccept = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    haptics.medium()
    try {
      const data: Record<string, any> = {}
      if (hasPaymentInfo && useExistingPayment && !showNewPbsFields) {
        // Using existing saved PBS — no new data to send
        data.use_existing_payment = true
      } else if (pbsReg && pbsAccount) {
        data.payment_reg = pbsReg.trim()
        data.payment_account = pbsAccount.trim()
      }
      data.fuldmagt_accepted = true
      if (confirmedAddress) data.confirmed_address = confirmedAddress
      if (confirmedPhone && keepNumber) data.confirmed_phone = confirmedPhone
      if (isMobile) data.keep_number = keepNumber
      if (extraAgreements.length > 0) {
        data.extra_agreements = extraAgreements
          .filter(a => a.address || a.phone || a.meter_number || !a.keep_number)
          .map(a => ({ ...a, keep_number: a.keep_number }))
      }

      await onAccept(data)
      haptics.success()
      setSuccessShown(true)
      // Auto-close after 3s — long enough for user to see confirmation
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current)
      autoCloseTimer.current = setTimeout(() => {
        setSuccessShown(false)
        onClose()
        // Feedback trigger after successful accept
        if (feedbackTimer.current) clearTimeout(feedbackTimer.current)
        feedbackTimer.current = setTimeout(() => {
          appEvents.emit('open-feedback', { trigger: 'quote_accepted', question: 'Var der noget du var i tvivl om?' })
        }, 5000)
      }, 3000)
    } catch {
      haptics.error()
      toast.error('Kunne ikke acceptere tilbud')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDismiss = useCallback(() => {
    onClose()
  }, [onClose])

  const renderBackdrop = useCallback(
    (props: any) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.5} pressBehavior="close" />
    ),
    []
  )

  const snapPoints = step === 'main' ? ['95%'] : step === 'pbs' ? ['70%'] : ['95%']

  // ─── PBS Step ───
  const validatePbsAndContinue = () => {
    const errors = new Set<string>()
    const reg = pbsReg.trim()
    const acct = pbsAccount.trim()

    if (!reg) errors.add('reg_empty')
    else if (reg.length !== 4) errors.add('reg_length')

    if (!acct) errors.add('account_empty')
    else if (acct.length < 4 || acct.length > 10) errors.add('account_length')

    if (errors.size > 0) {
      setPbsErrors(errors)
      haptics.error()
      return
    }
    setPbsErrors(new Set())
    haptics.light()
    setStep('fuldmagt')
  }

  const renderPbs = () => {
    const hasExisting = hasPaymentInfo && existingPaymentReg && existingPaymentAccount
    const regHasError = pbsErrors.has('reg_empty') || pbsErrors.has('reg_length')
    const accountHasError = pbsErrors.has('account_empty') || pbsErrors.has('account_length')

    return (
      <BottomSheetScrollView contentContainerStyle={styles.sheetContent} bounces={false}>
        <StepIndicator currentStep="pbs" />
        <View style={styles.centerIcon}>
          <Text style={{ fontSize: 28 }}>🏦</Text>
        </View>
        <Text style={styles.sheetTitle}>Betalingsoplysninger</Text>
        <Text style={styles.sheetSubtitle}>
          Bruges til automatisk betaling hos din nye udbyder.
        </Text>

        {hasExisting && !showNewPbsFields ? (
          <>
            {/* Existing saved account */}
            <View style={styles.existingPbsCard}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Check size={16} color={colors.success} />
                <Text style={{ fontSize: 14, fontWeight: '600', color: colors.text.primary }}>Gemt konto</Text>
              </View>
              <Text style={styles.existingPbsValues}>
                Reg: {existingPaymentReg}  ·  Konto: {existingPaymentAccount}
              </Text>
            </View>

            <Pressable
              style={styles.greenBtn}
              onPress={() => { haptics.light(); setStep('fuldmagt') }}
            >
              <Text style={styles.greenBtnText}>Brug denne konto</Text>
            </Pressable>
            <Pressable
              style={styles.skipBtn}
              onPress={() => { haptics.light(); setShowNewPbsFields(true) }}
            >
              <Text style={styles.skipBtnText}>Brug en anden konto</Text>
            </Pressable>
          </>
        ) : (
          <>
            {/* PBS input fields */}
            <View style={styles.pbsRow}>
              <View style={{ width: 90 }}>
                <Text style={styles.formLabel}>Reg.nr.</Text>
                <TextInput
                  style={[styles.formInput, regHasError && styles.formInputError]}
                  value={pbsReg}
                  onChangeText={t => { setPbsReg(t.replace(/\D/g, '')); setPbsErrors(prev => { const n = new Set(prev); n.delete('reg_empty'); n.delete('reg_length'); return n }) }}
                  maxLength={4}
                  placeholder="1234"
                  keyboardType="number-pad"
                  placeholderTextColor={colors.text.muted}
                />
                {pbsErrors.has('reg_empty') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Indtast reg.nr.</Text>
                  </View>
                )}
                {pbsErrors.has('reg_length') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Skal være 4 cifre</Text>
                  </View>
                )}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={styles.formLabel}>Kontonummer</Text>
                <TextInput
                  style={[styles.formInput, accountHasError && styles.formInputError]}
                  value={pbsAccount}
                  onChangeText={t => { setPbsAccount(t.replace(/\D/g, '')); setPbsErrors(prev => { const n = new Set(prev); n.delete('account_empty'); n.delete('account_length'); return n }) }}
                  maxLength={10}
                  placeholder="1234567890"
                  keyboardType="number-pad"
                  placeholderTextColor={colors.text.muted}
                />
                {pbsErrors.has('account_empty') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Indtast kontonummer</Text>
                  </View>
                )}
                {pbsErrors.has('account_length') && (
                  <View style={styles.inlineError}>
                    <AlertCircle size={12} color="#ef4444" />
                    <Text style={styles.inlineErrorText}>Skal være 4-10 cifre</Text>
                  </View>
                )}
              </View>
            </View>

            <Text style={styles.encryptNote}>
              {'\uD83D\uDD12'} Gemmes krypteret. Kan altid ændres senere i chatten.
            </Text>

            <Pressable
              style={styles.greenBtn}
              onPress={validatePbsAndContinue}
            >
              <Text style={styles.greenBtnText}>Gem og fortsæt</Text>
            </Pressable>

            {hasExisting && showNewPbsFields && (
              <Pressable
                style={styles.skipBtn}
                onPress={() => { haptics.light(); setShowNewPbsFields(false); setPbsReg(''); setPbsAccount(''); setPbsErrors(new Set()) }}
              >
                <Text style={styles.skipBtnText}>Brug gemt konto i stedet</Text>
              </Pressable>
            )}
          </>
        )}

        <Pressable style={styles.skipBtn} onPress={() => { haptics.light(); setStep('main') }}>
          <Text style={styles.skipBtnText}>Tilbage</Text>
        </Pressable>
      </BottomSheetScrollView>
    )
  }

  // ─── Fuldmagt Step ───
  const renderFuldmagt = () => {
    const d = quote.details || {}
    // Use outer scope displayPrice (tariff-adjusted for energy) — don't shadow it
    const fuldmagtPrice = displayPrice
    const prefix = getPricePrefix(quote)

    // Category-specific next steps
    const nextSteps: string[] = []
    if (isEnergy) {
      nextSteps.push('Vi sender fuldmagt til din nuværende udbyder')
      nextSteps.push('Målerskiftet sker automatisk — du skal ikke gøre noget')
      nextSteps.push('Du modtager besked når skiftet er gennemført')
    } else if (isMobile) {
      nextSteps.push('Vi sender fuldmagt til din nuværende udbyder')
      if (keepNumber) nextSteps.push('Dit nummer flyttes automatisk til ' + quote.provider)
      nextSteps.push('Du modtager nyt SIM-kort med posten')
      nextSteps.push('Aktiver SIM-kortet når du modtager det')
    } else if (isInternet) {
      nextSteps.push('Vi sender fuldmagt til din nuværende udbyder')
      if (d.router_included) nextSteps.push('Du modtager router fra ' + quote.provider)
      if (d.technology === 'fiber' || d.technology === 'Fiber') {
        nextSteps.push('En tekniker kan kontakte dig for installation')
      }
      nextSteps.push('Du modtager besked når skiftet er gennemført')
    }

    return (
    <BottomSheetScrollView contentContainerStyle={styles.sheetContent} bounces={false}>
      <StepIndicator currentStep="fuldmagt" />
      <View style={[styles.centerIcon, { backgroundColor: '#eff6ff' }]}>
        <Shield size={24} color={colors.primary} />
      </View>
      <Text style={styles.sheetTitle}>Bekræft skifte</Text>
      <Text style={styles.sheetSubtitle}>
        Du skifter til {quote.provider} på dine vegne.
      </Text>

      {/* Plan summary */}
      <View style={[styles.fuldmagtBox, { backgroundColor: '#f8fafc' }]}>
        <Text style={[styles.fuldmagtLabel, { marginBottom: 4 }]}>Din nye aftale</Text>
        <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text.primary }}>
          {quote.product_name || quote.provider}
        </Text>
        <Text style={{ fontSize: 14, color: colors.text.secondary, marginTop: 2 }}>
          {prefix}{Math.round(fuldmagtPrice).toLocaleString('da-DK')} kr/md
          {d.contract_length != null && ` · ${d.contract_length === 0 || String(d.contract_length).toLowerCase().includes('ingen') ? 'Ingen binding' : d.contract_length}`}
        </Text>
        {confirmedAddress ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 6 }}>
            <MapPin size={12} color={colors.text.muted} />
            <Text style={{ fontSize: 12, color: colors.text.secondary }}>{confirmedAddress}</Text>
          </View>
        ) : null}
        {confirmedPhone ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 3 }}>
            <Phone size={12} color={colors.text.muted} />
            <Text style={{ fontSize: 12, color: colors.text.secondary }}>{confirmedPhone}</Text>
          </View>
        ) : null}
        {(pbsReg && pbsAccount) || (hasPaymentInfo && !showNewPbsFields) ? (
          <Text style={{ fontSize: 12, color: colors.success, marginTop: 4 }}>
            {'\u2705'} PBS-betaling {pbsReg ? 'tilføjet' : 'gemt'}
          </Text>
        ) : null}
      </View>

      {/* Next steps */}
      {nextSteps.length > 0 && (
        <View style={[styles.fuldmagtBox, { backgroundColor: '#f0fdf4' }]}>
          <Text style={[styles.fuldmagtLabel, { color: '#15803d' }]}>Hvad sker der nu?</Text>
          {nextSteps.map((t, i) => (
            <View key={i} style={styles.fuldmagtItem}>
              <Text style={[styles.fuldmagtBullet, { color: '#16a34a' }]}>{i + 1}.</Text>
              <Text style={[styles.fuldmagtText, { color: '#166534' }]}>{t}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Legal consent */}
      <View style={styles.fuldmagtBox}>
        <Text style={styles.fuldmagtLabel}>Ved at acceptere bekræfter du:</Text>
        {[
          'ForbrugerAgenten må opsige din nuværende aftale',
          `ForbrugerAgenten må oprette ny aftale hos ${quote.provider}`,
          'Du har 14 dages fortrydelsesret',
          'Din identitet er bekræftet via MitID',
        ].map((t, i) => (
          <View key={i} style={styles.fuldmagtItem}>
            <Text style={styles.fuldmagtBullet}>•</Text>
            <Text style={styles.fuldmagtText}>{t}</Text>
          </View>
        ))}
      </View>

      <Pressable
        style={styles.checkboxRow}
        onPress={() => { haptics.selection(); setFuldmagtConsent(!fuldmagtConsent) }}
      >
        <View style={[styles.checkbox, fuldmagtConsent && styles.checkboxChecked]}>
          {fuldmagtConsent && <Check size={14} color="#fff" />}
        </View>
        <Text style={styles.checkboxLabel}>
          Jeg giver hermed fuldmagt til ForbrugerAgenten
        </Text>
      </Pressable>

      <Pressable
        style={[styles.greenBtn, (!fuldmagtConsent || isSubmitting) && { opacity: 0.5 }]}
        onPress={() => handleFinalAccept()}
        disabled={!fuldmagtConsent || isSubmitting}
      >
        {isSubmitting ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <ActivityIndicator size="small" color="#fff" />
            <Text style={styles.greenBtnText}>Behandler...</Text>
          </View>
        ) : (
          <Text style={styles.greenBtnText}>Bekræft og underskriv</Text>
        )}
      </Pressable>
      <Pressable
        style={styles.skipBtn}
        onPress={() => { haptics.light(); setStep('main'); setFuldmagtConsent(false) }}
      >
        <Text style={styles.skipBtnText}>Tilbage</Text>
      </Pressable>
    </BottomSheetScrollView>
    )
  }

  // ─── Main Accept Content ───
  const renderMain = () => (
    <BottomSheetScrollView contentContainerStyle={styles.mainSheetContent} bounces={false}>
      <StepIndicator currentStep="main" />
      {/* Header */}
      <View style={styles.header}>
        {providerLogo ? (
          <Image source={providerLogo} style={styles.providerLogo} />
        ) : (
          <View style={[styles.providerIcon, { backgroundColor: providerColor.bg }]}>
            <Text style={[styles.providerInitial, { color: providerColor.text }]}>
              {quote.provider.charAt(0)}
            </Text>
          </View>
        )}
        <View style={{ flex: 1 }}>
          <Text style={styles.providerName}>{quote.provider}</Text>
          {quote.product_name && (
            <Text style={styles.productName}>{quote.product_name}</Text>
          )}
        </View>
        <Pressable accessibilityRole="button" accessibilityLabel="Luk" onPress={onClose} style={styles.closeBtn} hitSlop={12}>
          <X size={20} color={colors.text.muted} />
        </Pressable>
      </View>

      {/* Address / Phone section */}
      {(showAddress || showPhone) && (
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            {showAddress ? (
              <MapPin size={14} color={colors.text.muted} />
            ) : (
              <Phone size={14} color={colors.text.muted} />
            )}
            <Text style={styles.sectionLabel}>
              {showAddress ? 'Leveringsadresser' : 'Telefonnumre'}
            </Text>
          </View>

          {/* (1) Primary */}
          <View style={styles.primaryPillRow}>
            <View style={styles.numberedPill}>
              <Text style={styles.numberedPillText}>1</Text>
            </View>
            <Text style={styles.primaryPillLabel}>Primær</Text>
          </View>

          {showAddress && (
            <View>
              <AddressInput
                value={confirmedAddress}
                onChange={setConfirmedAddress}
                placeholder="Søg din adresse..."
              />
              {!confirmedAddress.trim() && (
                <Text style={styles.requiredHint}>Påkrævet for at skifte udbyder</Text>
              )}
            </View>
          )}

          {showPhone && (
            <View>
              <Pressable
                style={styles.checkboxRow}
                onPress={() => { haptics.selection(); setKeepNumber(!keepNumber) }}
              >
                <View style={[styles.checkbox, keepNumber && styles.checkboxChecked]}>
                  {keepNumber && <Check size={14} color="#fff" />}
                </View>
                <Text style={styles.checkboxLabel}>Behold mit nuværende nummer</Text>
              </Pressable>

              {keepNumber ? (
                <View style={styles.phoneRow}>
                  <View style={styles.phonePrefix}>
                    <Text style={styles.phonePrefixText}>+45</Text>
                  </View>
                  <TextInput
                    style={[styles.formInput, { flex: 1 }]}
                    value={confirmedPhone}
                    onChangeText={t => {
                      const raw = t.replace(/[^0-9]/g, '').slice(0, 8)
                      setConfirmedPhone(raw.replace(/(\d{2})(?=\d)/g, '$1 ').trim())
                    }}
                    maxLength={11}
                    placeholder="12 34 56 78"
                    keyboardType="phone-pad"
                    placeholderTextColor={colors.text.muted}
                  />
                </View>
              ) : (
                <View style={styles.infoBox}>
                  <Text style={styles.infoBoxText}>
                    Du får tildelt et nyt nummer af den nye udbyder.
                  </Text>
                </View>
              )}

              {keepNumber && !confirmedPhone.replace(/\D/g, '') && (
                <Text style={styles.requiredHint}>Påkrævet for at skifte udbyder</Text>
              )}
            </View>
          )}

          {/* (2), (3), ... Extra agreements */}
          {extraAgreements.map((extra, idx) => (
            <View key={idx} style={styles.extraAgreementRow}>
              <View style={styles.extraAgreementHeader}>
                <View style={styles.extraAgreementLeft}>
                  <View style={styles.numberedPill}>
                    <Text style={styles.numberedPillText}>{idx + 2}</Text>
                  </View>
                  <Text style={styles.extraAgreementLabel}>
                    {showAddress ? 'Ekstra adresse' : 'Ekstra nummer'}
                  </Text>
                </View>
                <Pressable
                  onPress={() => setExtraAgreements(prev => prev.filter((_, i) => i !== idx))}
                  hitSlop={8}
                  style={styles.removeBtn}
                >
                  <Trash2 size={14} color={colors.text.muted} />
                </Pressable>
              </View>
              {showAddress && (
                <AddressInput
                  value={extra.address}
                  onChange={val => setExtraAgreements(prev => prev.map((a, i) => i === idx ? { ...a, address: val } : a))}
                  placeholder="Søg adresse..."
                />
              )}
              {showPhone && (
                <View style={styles.phoneRow}>
                  <View style={styles.phonePrefix}>
                    <Text style={styles.phonePrefixText}>+45</Text>
                  </View>
                  <TextInput
                    style={[styles.formInput, { flex: 1 }]}
                    value={extra.phone}
                    maxLength={11}
                    onChangeText={t => {
                      const raw = t.replace(/[^0-9]/g, '').slice(0, 8)
                      setExtraAgreements(prev => prev.map((a, i) => i === idx ? { ...a, phone: raw.replace(/(\d{2})(?=\d)/g, '$1 ').trim() } : a))
                    }}
                    placeholder="12 34 56 78"
                    keyboardType="phone-pad"
                    placeholderTextColor={colors.text.muted}
                  />
                </View>
              )}
              {isEnergy && (
                <TextInput
                  style={[styles.formInput, { marginTop: 6 }]}
                  value={extra.meter_number}
                  onChangeText={t => setExtraAgreements(prev => prev.map((a, i) => i === idx ? { ...a, meter_number: t } : a))}
                  placeholder="Målernummer (valgfrit)"
                  placeholderTextColor={colors.text.muted}
                />
              )}
            </View>
          ))}

          {/* Add extra agreement button */}
          <Pressable
            style={styles.addExtraBtn}
            onPress={() => { haptics.light(); setExtraAgreements(prev => [...prev, { address: '', phone: '', meter_number: '', keep_number: true }]) }}
          >
            <Plus size={14} color={colors.primary} />
            <Text style={styles.addExtraBtnText}>
              {showAddress ? 'Tilføj endnu en adresse' : 'Tilføj endnu et nummer'}
            </Text>
          </Pressable>
        </View>
      )}

      {/* Price */}
      <View style={styles.priceSection}>
        <View style={styles.priceRow}>
          <Text style={styles.priceLabel}>
            {isEnergy ? 'Est. månedspris' : 'Månedlig pris'}
          </Text>
          <Text style={styles.priceValue}>
            {getPricePrefix(quote)}{displayPrice.toLocaleString('da-DK')} kr/md
          </Text>
        </View>
        {(Number(quote.creation_fee) > 0 || Number(quote.details?.creation_fee_kr) > 0) && (
          <View style={styles.priceRow}>
            <Text style={styles.feeLabel}>Oprettelse</Text>
            <Text style={styles.feeValue}>
              {Number(quote.creation_fee || quote.details?.creation_fee_kr)} kr
            </Text>
          </View>
        )}
        {/* Energy breakdown */}
        {isEnergy && breakdown && (
          <View style={styles.breakdownDivider}>
            <EnergyBreakdownAccordion breakdown={breakdown} tariffs={tariffs || null} details={quote.details || {}} yearlyKwh={yearlyKwh} />
          </View>
        )}
        {/* Mobile breakdown */}
        {isMobile && (
          <View style={styles.breakdownDivider}>
            <MobileDetailAccordion details={quote.details || {}} showSpecs />
          </View>
        )}
        {/* Internet breakdown */}
        {isInternet && (
          <View style={styles.breakdownDivider}>
            <InternetDetailAccordion details={quote.details || {}} showSpecs />
          </View>
        )}
        {savings > 0 && (
          <View style={styles.savingsBanner}>
            <Text style={styles.savingsLabel}>Du sparer</Text>
            <Text style={styles.savingsValue}>
              {monthlySavings} kr/md · {Math.round(savings).toLocaleString('da-DK')} kr/år
            </Text>
          </View>
        )}
      </View>

      {/* Category-specific info */}
      {isEnergy && (
        <View style={[styles.infoCard, { backgroundColor: '#f0fdf4', borderColor: '#dcfce7' }]}>
          <Info size={16} color="#16a34a" />
          <Text style={[styles.infoCardText, { color: '#15803d' }]}>
            Alt fortsætter som det plejer — du får bare en billigere regning. Ingen afbrydelse, ingen ny installation.
          </Text>
        </View>
      )}
      {isMobile && (
        <View style={[styles.infoCard, { backgroundColor: '#eff6ff', borderColor: '#dbeafe' }]}>
          <Info size={16} color={colors.primary} />
          <Text style={[styles.infoCardText, { color: '#1d4ed8' }]}>
            Du modtager et nyt SIM-kort med posten og en SMS dagen før, du skal sætte det i. Dit nummer beholdes.
          </Text>
        </View>
      )}
      {isInternet && (
        <View style={[styles.infoCard, { backgroundColor: '#faf5ff', borderColor: '#f3e8ff' }]}>
          <Info size={16} color="#9333ea" />
          <Text style={[styles.infoCardText, { color: '#7c3aed' }]}>
            {quote.details?.technology === '5g' || quote.details?.technology === '5G'
              ? 'Du får tilsendt en router, du sætter i stikkontakten — ingen tekniker nødvendig.'
              : 'Skiftet sker automatisk. Ved behov for tekniker kontakter udbyderen dig direkte.'}
          </Text>
        </View>
      )}

      {/* Timeline */}
      <View style={styles.timelineSection}>
        <Text style={styles.timelineTitle}>Hvad sker der nu?</Text>
        {[
          {
            num: '1',
            label: `Vi kontakter ${quote.provider}`,
            sub: 'Vi håndterer alt på dine vegne',
          },
          {
            num: '2',
            label: 'Gammel aftale opsiges',
            sub: 'Vi klarer opsigelse og papirarbejde',
          },
          {
            num: '3',
            label: 'Bekræftelse i din indbakke',
            sub: 'Du modtager besked når skiftet er gennemført',
          },
        ].map((s, i) => (
          <View key={i} style={styles.timelineStep}>
            <View style={styles.timelineNum}>
              <Text style={styles.timelineNumText}>{s.num}</Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.timelineLabel}>{s.label}</Text>
              <Text style={styles.timelineSub}>{s.sub}</Text>
            </View>
          </View>
        ))}
      </View>

      {/* Trust signals */}
      <View style={styles.trustCard}>
        <View style={styles.trustHeader}>
          <Shield size={16} color={colors.primary} />
          <Text style={styles.trustTitle}>Skiftegaranti</Text>
        </View>
        {['14 dages fortrydelsesret', 'Ingen afbrydelse af service', 'Vi håndterer alt'].map((t, i) => (
          <View key={i} style={styles.trustItem}>
            <Check size={12} color={colors.primary} />
            <Text style={styles.trustText}>{t}</Text>
          </View>
        ))}
      </View>

      {/* CTA */}
      <View style={styles.ctaSection}>
        <Pressable accessibilityRole="button" accessibilityLabel={savings > 0 ? `Accepter tilbud og spar ${Math.round(savings)} kr om året` : `Accepter tilbud fra ${quote.provider}`} style={styles.greenBtn} onPress={handleConfirm}>
          <Text style={styles.greenBtnText}>
            {savings > 0
              ? `Accepter — spar ~${Math.round(savings).toLocaleString('da-DK')} kr/år`
              : `Accepter ${quote.provider}`}
          </Text>
        </Pressable>
        <Pressable accessibilityRole="button" accessibilityLabel="Afvis tilbuddet" style={styles.skipBtn} onPress={onClose}>
          <Text style={styles.skipBtnText}>Ikke nu</Text>
        </Pressable>
      </View>
    </BottomSheetScrollView>
  )

  // Conditional render: unmount BottomSheetModal when not visible to remove backdrop from DOM
  if (!visible) return null

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      snapPoints={snapPoints}
      onDismiss={handleDismiss}
      backdropComponent={renderBackdrop}
      handleIndicatorStyle={{ backgroundColor: '#D1D5DB', width: 40 }}
      backgroundStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
      enablePanDownToClose
      enableContentPanningGesture={Platform.OS !== 'web'}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="restore"
      containerStyle={isDesktop ? { left: 288 } as any : undefined}
    >
      {successShown && (
        <Animated.View entering={FadeIn.duration(250)} style={styles.successOverlay}>
          <Animated.View entering={ZoomIn.delay(100).springify()} style={styles.successCard}>
            <View style={styles.successCheckCircle}>
              <Check size={36} color="#fff" strokeWidth={3} />
            </View>
            {providerLogo ? (
              <Animated.Image entering={FadeIn.delay(200)} source={providerLogo} style={styles.successProviderLogo} />
            ) : (
              <Animated.View entering={FadeIn.delay(200)} style={[styles.successProviderIcon, { backgroundColor: providerColor.bg }]}>
                <Text style={[styles.successProviderInitial, { color: providerColor.text }]}>
                  {quote.provider.charAt(0)}
                </Text>
              </Animated.View>
            )}
            <Animated.Text entering={FadeIn.delay(300)} style={styles.successTitle}>
              Tilbud accepteret!
            </Animated.Text>
            <Animated.Text entering={FadeIn.delay(400)} style={styles.successSubtitle}>
              Vi s{'\u00E6'}tter skiftet til {quote.provider} i gang for dig
            </Animated.Text>
          </Animated.View>
        </Animated.View>
      )}
      {step === 'main' && renderMain()}
      {step === 'pbs' && renderPbs()}
      {step === 'fuldmagt' && renderFuldmagt()}
    </BottomSheetModal>
  )
}

// ─── Styles ──────────────────────────────────────

const styles = StyleSheet.create({
  sheetContent: {
    padding: spacing.lg,
    paddingBottom: Platform.OS === 'web' ? 40 : Platform.OS === 'ios' ? 48 : 32,
  },
  mainSheetContent: {
    paddingBottom: Platform.OS === 'web' ? 40 : Platform.OS === 'ios' ? 48 : 32,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  providerIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  providerLogo: {
    width: 40, height: 40, borderRadius: 20,
  },
  providerInitial: { fontWeight: '700', fontSize: 18 },
  providerName: { ...typography.bodyBold, color: colors.text.primary },
  productName: { ...typography.small, color: colors.text.secondary },
  closeBtn: { padding: 8, minWidth: 44, minHeight: 44, alignItems: 'center' as const, justifyContent: 'center' as const },

  // Sections
  section: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    padding: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
    gap: spacing.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  sectionLabel: { ...typography.captionBold, color: colors.text.secondary },

  // Form fields
  formLabel: { ...typography.small, color: colors.text.secondary, marginBottom: 4 },
  formInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    ...typography.body,
    color: colors.text.primary,
    backgroundColor: colors.background,
  },
  formInputError: { borderColor: '#ef4444' },
  inlineError: { flexDirection: 'row' as const, alignItems: 'center' as const, gap: 4, marginTop: 4 },
  inlineErrorText: { ...typography.small, color: '#ef4444' },
  requiredHint: { ...typography.small, color: colors.warning, marginTop: 2 },

  // Primary pill & extra agreements
  primaryPillRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  primaryPillLabel: { ...typography.small, fontWeight: '500', color: colors.text.secondary },
  numberedPill: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#dbeafe',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numberedPillText: { fontSize: 10, fontWeight: '700', color: colors.primary },
  extraAgreementRow: {
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: 6,
  },
  extraAgreementHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  extraAgreementLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  extraAgreementLabel: { ...typography.small, fontWeight: '500', color: colors.text.secondary },
  removeBtn: {
    padding: 4,
  },
  addExtraBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  addExtraBtnText: { ...typography.small, fontWeight: '500', color: colors.primary },

  // Breakdown divider
  breakdownDivider: {
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 8,
    marginTop: 8,
  },

  // Phone
  phoneRow: { flexDirection: 'row', gap: 8, alignItems: 'center' },
  phonePrefix: {
    paddingHorizontal: spacing.sm,
    paddingVertical: Platform.OS === 'ios' ? 10 : 8,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
  },
  phonePrefixText: { ...typography.body, color: colors.text.muted },

  // Checkbox
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  checkboxLabel: { ...typography.body, color: colors.text.primary, flex: 1, fontSize: 14 },

  // Info box
  infoBox: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
  },
  infoBoxText: { ...typography.small, color: colors.text.secondary },

  // Price
  priceSection: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    padding: spacing.md,
    backgroundColor: colors.surface,
    borderRadius: borderRadius.md,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  priceLabel: { ...typography.body, color: colors.text.secondary, fontSize: 14 },
  priceValue: { ...typography.bodyBold, color: colors.text.primary },
  feeLabel: { ...typography.small, color: colors.text.muted },
  feeValue: { ...typography.small, color: colors.text.secondary },
  savingsBanner: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#dcfce7',
    borderRadius: borderRadius.sm,
  },
  savingsLabel: { ...typography.captionBold, color: '#15803d' },
  savingsValue: { ...typography.captionBold, color: '#15803d' },

  // Info card
  infoCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    padding: spacing.sm,
    borderRadius: borderRadius.md,
    borderWidth: 1,
  },
  infoCardText: { ...typography.small, flex: 1 },

  // Timeline
  timelineSection: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    gap: spacing.sm,
  },
  timelineTitle: { ...typography.bodyBold, color: colors.text.primary },
  timelineStep: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.sm },
  timelineNum: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#dbeafe',
    alignItems: 'center',
    justifyContent: 'center',
  },
  timelineNumText: { ...typography.small, fontWeight: '700', color: colors.primary },
  timelineLabel: { ...typography.bodyBold, color: colors.text.primary, fontSize: 14 },
  timelineSub: { ...typography.small, color: colors.text.secondary },

  // Trust
  trustCard: {
    marginHorizontal: spacing.md,
    marginTop: spacing.md,
    padding: spacing.sm,
    backgroundColor: '#eff6ff',
    borderWidth: 1,
    borderColor: '#dbeafe',
    borderRadius: borderRadius.md,
  },
  trustHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 },
  trustTitle: { ...typography.bodyBold, color: '#1e3a5f', fontSize: 14 },
  trustItem: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 },
  trustText: { ...typography.small, color: '#1d4ed8' },

  // CTA
  ctaSection: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    gap: 8,
  },
  greenBtn: {
    paddingVertical: 14,
    backgroundColor: colors.ctaGreen,
    borderRadius: borderRadius.md,
    alignItems: 'center',
    ...shadows.colored(colors.ctaGreen),
  },
  greenBtnText: { ...typography.bodyBold, color: '#fff', fontSize: 15 },
  skipBtn: { paddingVertical: 10, alignItems: 'center' },
  skipBtnText: { ...typography.body, color: colors.text.secondary, fontSize: 14 },

  // PBS
  centerIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#eff6ff',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: spacing.sm,
  },
  sheetTitle: { ...typography.h3, color: colors.text.primary, textAlign: 'center' },
  sheetSubtitle: {
    ...typography.small,
    color: colors.text.secondary,
    textAlign: 'center',
    marginTop: 4,
    marginBottom: spacing.md,
  },
  pbsRow: { flexDirection: 'row', gap: 8, marginBottom: 8 },
  encryptNote: { ...typography.small, color: colors.text.muted, marginBottom: spacing.md },
  warningBox: {
    backgroundColor: '#fffbeb',
    borderWidth: 1,
    borderColor: '#fde68a',
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    marginBottom: 8,
  },
  warningText: { ...typography.small, color: '#b45309', textAlign: 'center' },
  existingPbsCard: {
    backgroundColor: '#f0fdf4',
    borderWidth: 1,
    borderColor: '#bbf7d0',
    borderRadius: borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
  },
  existingPbsValues: {
    fontSize: 15,
    fontWeight: '500' as const,
    color: colors.text.primary,
    fontVariant: ['tabular-nums'] as any,
  },

  // Success overlay
  successOverlay: {
    position: 'absolute' as const,
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    zIndex: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  successCard: {
    backgroundColor: '#fff',
    borderRadius: borderRadius.xl,
    padding: 32,
    alignItems: 'center' as const,
    width: 280,
    ...shadows.lg,
  },
  successCheckCircle: {
    width: 72,
    height: 72,
    borderRadius: 36,
    backgroundColor: '#22c55e',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    marginBottom: 16,
  },
  successProviderLogo: {
    width: 48,
    height: 48,
    borderRadius: 12,
    marginBottom: 16,
  },
  successProviderIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    marginBottom: 16,
  },
  successProviderInitial: {
    fontSize: 20,
    fontWeight: '700' as const,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: '#111827',
    textAlign: 'center' as const,
    marginBottom: 4,
  },
  successSubtitle: {
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center' as const,
  },

  // Fuldmagt
  fuldmagtBox: {
    backgroundColor: colors.surface,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    marginBottom: spacing.md,
  },
  fuldmagtLabel: { ...typography.captionBold, color: colors.text.primary, marginBottom: 8 },
  fuldmagtItem: { flexDirection: 'row', gap: 6, marginBottom: 4 },
  fuldmagtBullet: { ...typography.small, color: colors.text.secondary },
  fuldmagtText: { ...typography.small, color: colors.text.secondary, flex: 1 },
})
